import { getUsageMode } from './utils/usageMode';

// What Cato needs from its host in Solvie. A package is built once and runs in every environment, so the shell
// passes these in (its env files hold the values) instead of Cato reading import.meta.env.
// Deliberately not here: the page title (the shell owns the document title).
export type CatoSettings = {
  ccpInstanceUrl: string;
  samlUrl: string;
  cassieWebsocketUrl: string;
  cassieWebsocketProtocol: string;
  region: string;
  environment?: string;
  catoApiUrl?: string;
  emailApiUrl?: string;
  emailContactFlowId?: string;
  chatApiUrl?: string;
  muiXLicenseKey?: string;
  chatRingtoneUrl?: string;
  // The Generate Chat buttons' login, for development and testing builds only: in Testing it is a secret that ends up
  // in the bundle.
  chatApiAuthUrl?: string;
  chatApiAuthUsername?: string;
  chatApiAuthPassword?: string;
  // Still open with Contact Assistance (CX-6668): the shell owns RUM, and Cato may drop its own Datadog tokens
  datadogEnv?: string;
  datadogClientToken?: string;
  datadogRumClientToken?: string;
};

const REQUIRED_SETTINGS = [
  'ccpInstanceUrl',
  'samlUrl',
  'cassieWebsocketUrl',
  'cassieWebsocketProtocol',
  'region'
] as const satisfies readonly (keyof CatoSettings)[];

let injectedSettings: CatoSettings | undefined;

export const setCatoSettings = (settings: CatoSettings): void => {
  injectedSettings = settings;
};

// Throws when a required setting is missing. Called when Cato's pane mounts, so the failure lands in the shell's
// error boundary for that pane instead of stopping the shell while it loads.
export const assertCatoSettings = (settings: Partial<CatoSettings> | undefined): void => {
  const missing = REQUIRED_SETTINGS.filter(key => !settings?.[key]);

  if (missing.length > 0) {
    throw new Error(`Cato is missing required settings: ${missing.join(', ')}`);
  }
};

const getEnvironmentVariable = (environmentVariable: string, isOptional = false): string => {
  const value = import.meta.env[environmentVariable] as string | undefined;

  if (!value && !isOptional) {
    throw new Error(`Couldn't find environment variable: ${environmentVariable}`);
  }

  return typeof value === 'string' ? value : '';
};

// In solvie mode the value comes from the settings prop (no env name: not a Solvie setting), otherwise from
// import.meta.env. Read on access, never at import time, so nothing throws before Cato's pane has its settings.
const read = (environmentVariable: string, solvieKey: keyof CatoSettings | undefined, isOptional = false): string => {
  if (getUsageMode() !== 'solvie') {
    return getEnvironmentVariable(environmentVariable, isOptional);
  }

  if (!injectedSettings) {
    throw new Error('Cato was rendered in Solvie without its settings prop');
  }

  const value = solvieKey ? injectedSettings[solvieKey] : undefined;

  if (!value && !isOptional) {
    throw new Error(`Cato is missing required setting: ${String(solvieKey)}`);
  }

  return value ?? '';
};

const config = {
  get ccpInstanceURL() {
    return read('VITE_CCP_INSTANCE_URL', 'ccpInstanceUrl');
  },
  get cppSamURL() {
    return read('VITE_SAML_URL', 'samlUrl');
  },
  get cassieWebsocketURL() {
    return read('VITE_CASSIE_WS_URL', 'cassieWebsocketUrl');
  },
  get cassieWebsocketProtocol() {
    return read('VITE_CASSIE_PROTOCOL', 'cassieWebsocketProtocol');
  },
  get title() {
    return read('VITE_TITLE', undefined, getUsageMode() === 'solvie');
  },
  get region() {
    return read('VITE_REGION', 'region');
  },
  get environment() {
    return read('VITE_ENV', 'environment', true);
  },
  get catoApiUrl() {
    return read('VITE_CATO_API_URL', 'catoApiUrl', true);
  },
  get emailApiUrl() {
    return read('VITE_EMAIL_API_URL', 'emailApiUrl', true);
  },
  get emailContactFlowId() {
    return read('VITE_EMAIL_CONTACT_FLOW_ID', 'emailContactFlowId', true);
  },
  get chatApiAuthUrl() {
    return read('VITE_CHAT_AUTH_URL', 'chatApiAuthUrl', true);
  },
  get chatApiUrl() {
    return read('VITE_CHAT_API_URL', 'chatApiUrl', true);
  },
  get chatRingtoneUrl() {
    return read('VITE_CHAT_RINGTONE_URL', 'chatRingtoneUrl', true);
  },
  get chatApiAuthUsername() {
    return read('VITE_CHAT_AUTH_USERNAME', 'chatApiAuthUsername', true);
  },
  get chatApiAuthPassword() {
    return read('VITE_CHAT_AUTH_PASSWORD', 'chatApiAuthPassword', true);
  },
  get muiXLicenseKey() {
    return read('VITE_MUI_X_LICENSE_KEY', 'muiXLicenseKey', true);
  },
  get datadogClientToken() {
    return read('VITE_DATADOG_CLIENT_TOKEN', 'datadogClientToken', true);
  },
  get datadogRumClientToken() {
    return read('VITE_DATADOG_RUM_CLIENT_TOKEN', 'datadogRumClientToken', true);
  },
  get datadogEnv() {
    return read('VITE_DATADOG_ENV', 'datadogEnv', true);
  }
};

export default config;
